import { useState } from 'react';
import type { ActivityFeed } from '../../agentic/activity-feed';
import { feedAgentCount } from '../../agentic/activity-feed';
import './styles.css';

export interface AgenticActivityFeedProps {
  feed: ActivityFeed;
  /**
   * True while the turn is still streaming: the feed renders expanded inside
   * a polite live region so new rows are announced (P2-09 pattern). After
   * done it renders collapsed behind a disclosure button.
   */
  live: boolean;
}

/**
 * Agentic activity feed (P3-06): grouped by agent — `agent_started` opened
 * each group, `handoff` renders a transition row, `tool_call`/`status` rows
 * sit within their group. Collapses when the turn is done.
 *
 * Single-group feeds render as the flat row list Phase 2 shipped (no group
 * header) so plain specialist turns look exactly as before; headers appear
 * only when more than one agent took part.
 */
export function AgenticActivityFeed({ feed, live }: AgenticActivityFeedProps) {
  const [expanded, setExpanded] = useState(false);
  if (!feed.items.length) return null;

  const agents = feedAgentCount(feed);
  const showHeaders = agents > 1;
  const collapsed = feed.done && !expanded;

  const list = (
    <ul
      className="cs-agentic-activity"
      aria-live={live ? 'polite' : undefined}
    >
      {feed.items.map((item, i) => {
        if (item.kind === 'handoff') {
          return (
            <li
              key={`h-${i}`}
              className="cs-agentic-activity__row cs-agentic-activity__handoff"
            >
              Handed off to {item.toName}
            </li>
          );
        }
        return (
          <li key={`g-${i}`} className="cs-agentic-activity__group">
            {showHeaders && (
              <div className="cs-agentic-activity__agent">
                <span>{item.name}</span>
                {item.role === 'orchestrator' && (
                  <span className="cs-agentic-activity__role"> · coordinator</span>
                )}
              </div>
            )}
            {item.rows.length > 0 && (
              <ul className="cs-agentic-activity__rows">
                {item.rows.map((row, j) => (
                  <li key={`r-${i}-${j}`} className="cs-agentic-activity__row">
                    {row}
                  </li>
                ))}
              </ul>
            )}
          </li>
        );
      })}
    </ul>
  );

  if (!feed.done) return list;

  const summary =
    `Activity: ${feed.steps} step${feed.steps === 1 ? '' : 's'}` +
    (agents > 1 ? ` across ${agents} assistants` : '');

  return (
    <div className="cs-agentic-activity-done">
      <button
        type="button"
        className="cs-agentic-activity__toggle"
        aria-expanded={expanded}
        onClick={() => setExpanded((v) => !v)}
      >
        <span aria-hidden="true">{expanded ? '▾' : '▸'} </span>
        {summary}
      </button>
      {!collapsed && list}
    </div>
  );
}
